import React, { useState } from "react";
import { Box, Button, Input, Text, useBoolean, VStack } from "@chakra-ui/react";
import SimplePopup from "components/SimplePopup";
import useTranslate from "hooks/useTranslate";
import { formatEtherBalance } from "utils/numberUtils";
import CopyInput from "components/CopyInput";
import V1MultiSelect from "components/V1MultiSelect";
import InfoTile from "components/InfoTile";
import { reservePrice, reserveV2Wallet } from "config/sales";
import { v1Name, v2Name } from "config";
import { plural } from "pluralize";
import * as reserveApi from "api/reserveApi";
import useSnackbar from "hooks/useSnackbar";
import TermsConsentPopup from "components/TermsConsentPopup";

type ReserveProcessContainerProps = {
  trigger: JSX.Element;
};

const ReserveProcessContainer = ({ trigger }: ReserveProcessContainerProps) => {
  const translate = useTranslate();
  const [tokenIds, setTokenIds] = useState<number[]>();
  const [address, setAddress] = useState<string>();
  const snackbar = useSnackbar();

  const [isInstructionsPopupOpen, isInstructionsPopup] = useBoolean();
  const [isTermsConsentPopupOpen, setIsTermsConsentPopupOpen] = useBoolean();
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useBoolean();

  const handleConfirm = () => {
    if (!tokenIds || !address || address.length !== 42) {
      snackbar("error", translate("reserve:invalid", { v1Name }));
      return;
    }

    isInstructionsPopup.off();
    setIsTermsConsentPopupOpen.on();
  };

  const handleTermsConsent = async () => {
    setIsTermsConsentPopupOpen.off();

    try {
      await reserveApi.reserveTokens({
        tokenIds: tokenIds!,
        address: address!,
      });

      setIsSuccessPopupOpen.on();
    } catch (error: any) {
      snackbar("error", error?.message || translate("error:default"));
    }

    setIsSuccessPopupOpen.on();
  };

  return (
    <>
      <Box>
        {React.cloneElement(trigger, {
          onClick: isInstructionsPopup.on,
        })}
      </Box>

      {isInstructionsPopupOpen && (
        <SimplePopup
          isOpen
          title={translate("reserve:title", {
            v2Name,
          })}
          onClose={isInstructionsPopup.off}
        >
          <VStack mt="2" spacing="4">
            <Text textAlign="center" fontSize="sm" opacity="0.8" maxW="25em">
              {translate("reserve:description", { v1Name })}
            </Text>

            <VStack w="full" spacing="2px">
              {new Array(4).fill("reserve:step").map((key, index) => (
                <InfoTile
                  key={index + 1}
                  index={index + 1}
                  text={translate(`${key}:${index + 1}`, {
                    price: formatEtherBalance(reservePrice),
                    currency: "ETH",
                    v1Name: v1Name,
                    v2Name: v2Name,
                  })}
                />
              ))}
            </VStack>

            <VStack w="full" spacing="3">
              <Input
                placeholder={translate("reserve:address:placeholder", {
                  v1Name,
                })}
                size="lg"
                onChange={(event) => setAddress(event.target.value)}
              />

              <Box w="full">
                <V1MultiSelect
                  placeholder={translate("reserve:tokenIds:placeholder", {
                    v1Name: plural(v1Name),
                  })}
                  onChange={(tokenIds) => setTokenIds(tokenIds)}
                />
              </Box>
            </VStack>

            <Button w="full" onClick={handleConfirm}>
              {translate("reserve:confirm")}
            </Button>
          </VStack>
        </SimplePopup>
      )}

      {tokenIds && address && isTermsConsentPopupOpen && (
        <TermsConsentPopup
          isOpen
          isSignatureRequired={false}
          address={address}
          tokenIds={tokenIds}
          onSuccess={handleTermsConsent}
          onClose={setIsTermsConsentPopupOpen.off}
        />
      )}

      {isSuccessPopupOpen && (
        <SimplePopup
          isOpen
          title={translate("reserve:success:title")}
          onClose={setIsSuccessPopupOpen.off}
        >
          <Box>
            <VStack p="6" bg="dark.800" borderRadius="md" spacing="4" mt="5">
              <Text
                textAlign="center"
                maxW="20em"
                fontSize="sm"
                dangerouslySetInnerHTML={{
                  __html: translate("reserve:success:description", {
                    price: formatEtherBalance(
                      reservePrice.mul(tokenIds?.length || 1)
                    ),
                    currency: "ETH",
                    v1Name,
                  }),
                }}
              />

              <CopyInput
                value={reserveV2Wallet}
                bg="dark.900"
                borderRadius="lg"
              />

              <Text textAlign="center" maxW="20em" fontSize="sm" opacity="0.6">
                {translate("reserve:success:note")}
              </Text>
            </VStack>

            <Button onClick={setIsSuccessPopupOpen.off} mt="4" w="full">
              {translate("reserve:success:ok")}
            </Button>
          </Box>
        </SimplePopup>
      )}
    </>
  );
};

export default ReserveProcessContainer;
